import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MedicineService } from 'src/core/services/medicine.service';
import { DosageUnit } from 'src/interfaces/medicine';

@Component({
  selector: 'core-dosage-unit-search-field',
  templateUrl: './dosage-unit-search-field.component.html',
  styleUrls: ['./dosage-unit-search-field.component.scss'],
  standalone: false
})
export class DosageUnitSearchFieldComponent implements OnInit, OnDestroy {

  @Input({ required: true }) control!: AbstractControl;
  @Input() label = 'Unit';
  @Output() dosageUnitCreated = new EventEmitter<DosageUnit>();

  dosageUnits: DosageUnit[] = [];
  isSearching = false;

  private readonly destroy$ = new Subject<void>();
  private readonly searchLimit = 50;

  constructor(private _medicineService: MedicineService) { }

  ngOnInit(): void {
    this.loadDosageUnits('');

    const currentValue = this.control.value as DosageUnit | string | null;
    if (currentValue && typeof currentValue === 'object') {
      this.ensureSelectedInList(currentValue);
    }

    this.control.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: DosageUnit | string | null) => {
        if (typeof value === 'string') {
          this.loadDosageUnits(value);
        } else if (value && typeof value === 'object') {
          this.ensureSelectedInList(value);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  displayDosageUnit = (unit: DosageUnit | string | null): string => {
    if (!unit) {
      return '';
    }

    return typeof unit === 'string' ? unit : unit.unit;
  };

  onSearchInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const currentValue = this.control.value as DosageUnit | string | null;

    if (!currentValue || typeof currentValue === 'object') {
      this.control.setValue(inputValue, { emitEvent: true });
    }
  }

  async loadDosageUnits(search: string): Promise<void> {
    this.isSearching = true;

    try {
      this.dosageUnits = await this._medicineService.getDosageUnits(1, this.searchLimit, search);

      const currentValue = this.control.value as DosageUnit | string | null;
      if (currentValue && typeof currentValue === 'object') {
        this.ensureSelectedInList(currentValue);
      }
    } finally {
      this.isSearching = false;
    }
  }

  async createDosageUnit(event: Event): Promise<void> {
    event.stopPropagation();

    const newDosageUnit: DosageUnit = await this._medicineService
      .openDosageUnitCreateDialog(
        'New dosage unit',
        'Create a new dosage unit'
      );

    if (!newDosageUnit) {
      return;
    }

    this.ensureSelectedInList(newDosageUnit);
    this.control.setValue(newDosageUnit);
    this.dosageUnitCreated.emit(newDosageUnit);
  }

  private ensureSelectedInList(dosageUnit: DosageUnit): void {
    if (!this.dosageUnits.some(unit => unit.id === dosageUnit.id)) {
      this.dosageUnits = [dosageUnit, ...this.dosageUnits];
    }
  }

}
